import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Package, Search } from 'lucide-react';
import { adminApi } from '@/services/adminApi';
import type { AdminOrderListItem, AdminOrdersResponse } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, type OrderStatus, type PaymentStatus } from '@printtat/shared';
import { formatPrice } from '@printtat/shared';
import { PaymentMethodLabel, PageHeader, PageContent, EmptyState, LoadingState, Button, Table, Tabs, StatusBadge } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'in_production', label: 'In Production' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

const ORDER_STATUS_COLORS: Record<string, 'yellow' | 'blue' | 'indigo' | 'violet' | 'purple' | 'cyan' | 'green' | 'red' | 'orange' | 'gray'> = {
  pending: 'yellow', confirmed: 'blue', in_production: 'indigo', quality_check: 'violet',
  shipped: 'purple', out_for_delivery: 'cyan', delivered: 'green', cancelled: 'red', returned: 'orange',
};

const PAYMENT_STATUS_COLORS: Record<string, 'yellow' | 'amber' | 'green' | 'red' | 'gray' | 'orange'> = {
  pending: 'yellow', awaiting_payment: 'amber', paid: 'green', failed: 'red', refund_pending: 'orange', refunded: 'gray',
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const AdminOrdersPage = () => {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });

  const limit = 20;

  const { data, isLoading } = useQuery<AdminOrdersResponse>({
    queryKey: [...queryKeys.orders.all(), { status: statusFilter, search, page, limit, ...sort }],
    queryFn: () => adminApi.orders.list({
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: search || undefined,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
  });

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<AdminOrderListItem>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      render: (o) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{o.orderNumber}</p>
          <p className="text-xs text-gray-500">{o.customerName}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (o) => <StatusBadge label={ORDER_STATUS_LABELS[o.status as OrderStatus] ?? o.status} color={ORDER_STATUS_COLORS[o.status] ?? 'gray'} size="xs" />,
    },
    {
      key: 'items',
      header: 'Items',
      render: (o) => <span className="text-sm text-gray-600">{o.itemCount}</span>,
    },
    {
      key: 'totalAmount',
      header: 'Total',
      sortable: true,
      render: (o) => <span className="text-sm font-medium text-gray-900">{formatPrice(o.totalAmount)}</span>,
    },
    {
      key: 'payment',
      header: 'Payment',
      render: (o) => (
        <div className="space-y-1">
          <StatusBadge label={PAYMENT_STATUS_LABELS[o.paymentStatus as PaymentStatus] ?? o.paymentStatus} color={PAYMENT_STATUS_COLORS[o.paymentStatus] ?? 'gray'} size="xs" />
          <div className="text-xs text-gray-500">
            <PaymentMethodLabel
              paymentMethod={o.paymentMethod}
              paymentMethodDetail={o.paymentMethodDetail}
              cardBrand={o.cardBrand}
              cardLastFour={o.cardLastFour}
              iconSize="w-3 h-3"
            />
          </div>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (o) => <span className="text-sm text-gray-500">{formatDate(o.createdAt)}</span>,
    },
  ];

  return (
    <PageContent>
      <PageHeader
        icon={Package}
        title="Orders"
        description={`${total} total order${total !== 1 ? 's' : ''}`}
      />

      {/* Filters row */}
      <div className="mt-6 flex items-center justify-between gap-4">
        <Tabs
          tabs={STATUS_TABS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          variant="pill"
        />

        <div className="relative min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order # or customer..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      {/* Orders table */}
      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : orders.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No orders found"
            description={search || statusFilter !== 'all' ? 'Try adjusting your filters' : undefined}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={orders}
              keyExtractor={(o) => o.id}
              onRowClick={(o) => navigate(`/orders/${o.id}`)}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  Previous
                </Button>
                <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </PageContent>
  );
};
